import { useState, useMemo } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { attorneys } from "@/data/attorneys";
import type { Attorney } from "@/data/attorneys";

// Extract unique sorted values
const allPracticeAreas = [...new Set(attorneys.flatMap((a) => a.practiceAreas))].sort();
const allLicenses = [...new Set(attorneys.flatMap((a) => a.licenses))].sort();
const allTitles = [...new Set(attorneys.map((a) => a.title).filter(Boolean) as string[])].sort();
const allLocations = [...new Set(
  attorneys.flatMap((a) => a.location ? a.location.split(",").map((l) => l.trim()) : [])
)].sort();

interface AttorneySearchBarProps {
  onFilter: (filtered: Attorney[]) => void;
}

const AttorneySearchBar = ({ onFilter }: AttorneySearchBarProps) => {
  const [search, setSearch] = useState("");
  const [selectedPractice, setSelectedPractice] = useState("");
  const [selectedLicense, setSelectedLicense] = useState("");
  const [selectedTitle, setSelectedTitle] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");

  useMemo(() => {
    const q = search.toLowerCase().trim();
    const filtered = attorneys.filter((a) => {
      if (q && !a.name.toLowerCase().includes(q)) return false;
      if (selectedPractice && !a.practiceAreas.includes(selectedPractice)) return false;
      if (selectedLicense && !a.licenses.includes(selectedLicense)) return false;
      if (selectedTitle) {
        const isPartnerException = selectedTitle === "Partner" && 
          (a.name === "Joseph E. Vaughan" || a.name === "Tracey A. McLean");
        if (a.title !== selectedTitle && !isPartnerException) return false;
      }
      if (selectedLocation && !(a.location && a.location.split(",").map((l) => l.trim()).includes(selectedLocation))) return false;
      return true;
    });
    onFilter(filtered);
  }, [search, selectedPractice, selectedLicense, selectedTitle, selectedLocation, onFilter]);

  const hasFilters = search || selectedPractice || selectedLicense || selectedTitle || selectedLocation;

  const clearAll = () => {
    setSearch("");
    setSelectedPractice("");
    setSelectedLicense("");
    setSelectedTitle("");
    setSelectedLocation("");
  };

  const selectClass = "h-10 rounded-md border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className="mb-12 space-y-4">
      {/* Row 1: Name search + Practice Area */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground"
          />
        </div>

        <select
          value={selectedPractice}
          onChange={(e) => setSelectedPractice(e.target.value)}
          className={`${selectClass} sm:w-64`}
        >
          <option value="">All Practice Areas</option>
          {allPracticeAreas.map((pa) => (
            <option key={pa} value={pa}>{pa}</option>
          ))}
        </select>
      </div>

      {/* Row 2: Role, Location, Jurisdiction */}
      <div className="flex flex-col sm:flex-row gap-4">
        <select
          value={selectedTitle}
          onChange={(e) => setSelectedTitle(e.target.value)}
          className={`${selectClass} sm:w-48`}
        >
          <option value="">All Titles</option>
          {allTitles.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        <select
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
          className={`${selectClass} sm:w-52`}
        >
          <option value="">All Locations</option>
          {allLocations.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>

        <select
          value={selectedLicense}
          onChange={(e) => setSelectedLicense(e.target.value)}
          className={`${selectClass} sm:w-48`}
        >
          <option value="">Licensure</option>
          {allLicenses.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
      </div>

      {hasFilters && (
        <button
          onClick={clearAll}
          className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-body text-muted-foreground hover:text-primary transition-colors"
        >
          <X className="w-3 h-3" /> Clear filters
        </button>
      )}
    </div>
  );
};

export default AttorneySearchBar;
